'use client'
import BreadCrumb from '@/components/BreadCrumb';
import { resolveDeityBySlug } from '@/services/app.services'
import React, { useEffect, useRef, useState } from 'react'
import Image from 'next/image';
import { noto_sans, noto_serif, ubuntu, } from '@/globalConstants';
import { Pagination, Skeleton } from '@mui/material';
import { usePathname } from 'next/navigation';
import { DeityInfo, Prarthana } from '@/globalTypes';
import AudioPlayer from '../AudioPlayer';
import PageBanner from '../PageBanner';

const BASE_CLASS = "parathana"
const EMPTY_DEITY: DeityInfo = {
    "title": "",
    "description": "",
    "image_url": "",
    "prarthanas": [],
    "is_audio_available": false,
    "ui_info": {
        "image_url": ""
    }
};

// `initialDeity` is resolved by the server page, so the title, description and
// prarthana list are in the server HTML. Without it the first render was all
// skeletons, and MUI's Skeleton injected its <style> into the <h1>, so crawlers
// read CSS as the deity name.
const DeityView = ({ deitySlug, initialDeity }: { deitySlug: string, initialDeity?: DeityInfo }) => {
    const pathname = usePathname();
    const [paginationNumber, setPaginationNumber] = useState(1);
    const [audioCurrentlyPlaying, setAudioCurrentlyPlaying] = useState<Prarthana>(
        {
            "id": "",
            "title": "",
            "audio_url": "",
            "hls_audio_url": "",
            "image_url": "",
            "banner_image_url": "",
            "duration": "",
            "is_audio_available": true,
            "ui_info": {
                "image_url": "",
                "banner_image_url": ""
            }
        }
    );
    const currentAudioTagRef = useRef<{ audioTag: any, setIsPlaying: any }>({
        audioTag: undefined,
        setIsPlaying: () => { },
    });
    const [deity, setDeity] = useState<DeityInfo>(initialDeity ?? EMPTY_DEITY)
    useEffect(() => {

        if (!initialDeity) (async () => {
            // Same resolver the server page used for its 404 check, so the client
            // fetch can't disagree with it about which deity this URL means.
            try {
                let deity = await resolveDeityBySlug(deitySlug);
                if (deity) setDeity(deity);
            } catch (error) {
                console.error(error);
            }

        })()
        const pageNumber = JSON.parse(localStorage.getItem("prarthanaPageNumber") || '1');
        setPaginationNumber(pageNumber);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const updateCurrentPrarthana = (audio: HTMLAudioElement, action: string, prarthanaDetails: Prarthana, setIsPlaying: any) => {
        if (audioCurrentlyPlaying.id === prarthanaDetails.id) {
            if (action === 'pause') {
                audio.pause();
                setIsPlaying(false);
            } else {
                currentAudioTagRef.current.audioTag.play();
                currentAudioTagRef.current.setIsPlaying(true);
            }
        } else {
            if (currentAudioTagRef.current.audioTag) {
                currentAudioTagRef.current.audioTag.pause();
                currentAudioTagRef.current.setIsPlaying(false);

            }
            currentAudioTagRef.current = { audioTag: audio, setIsPlaying: setIsPlaying };
            setAudioCurrentlyPlaying(prarthanaDetails);
            audio.play();
            setIsPlaying(true);
        }

    }

    const handlePageChange = (pagenumber: number) => {
        setPaginationNumber(pagenumber);
    }

    useEffect(() => {
        localStorage.setItem('prarthanaPageNumber', JSON.stringify(paginationNumber));

        return (() => {
            localStorage.removeItem('prarthanaPageNumber');
        })
    }, [paginationNumber])

    return (
        <>
            <div className="w-full">
                <PageBanner
                    breadcrumb={<BreadCrumb pathname={pathname} />}
                    title={deity.title.length ? deity.title : <Skeleton variant="text" width={'140px'} height={'2.5rem'} />}
                    align="center"
                />
            </div>
            <div className='mb-20'></div>
            <div className={`${BASE_CLASS} bg-[#F8EFE0] pt-0 pb-[4rem] md:pb-[4.5rem] flex place-content-center`}>
                <div className={`${BASE_CLASS}__container grid gap-[1.25rem] md:gap-[1.75rem] w-[95%] md:w-[92%] 2xl:w-[75%]`}>

                    {/* Top hero section */}
                    <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-[20px] lg:gap-[36px] items-start">
                        <div className="w-full">
                            {deity.image_url.length ? (
                                <div className="w-full rounded-[24px] overflow-hidden bg-white shadow-[0_10px_30px_rgba(0,0,0,0.12)] border border-[#F4DBC0]">
                                    <Image
                                        src={deity.ui_info.image_url || deity.image_url}
                                        alt="Deity Banner"
                                        width={1}
                                        height={1}
                                        className="w-full h-[260px] sm:h-[340px] lg:h-[480px] object-cover"
                                        priority={true}
                                    />
                                </div>
                            ) : (
                                <Skeleton variant="rounded" width={'100%'} height={'320px'} className="lg:block" />
                            )}
                        </div>

                        <div className="h-full">
                            <div className="bg-white rounded-[20px] p-[1.25rem] lg:p-[1.5rem] shadow-[0_8px_28px_rgba(0,0,0,0.08)] border border-[#F1D7B8] h-full">
                                <h2 className={`${ubuntu.className} font-[600] text-[22px] lg:text-[26px] text-[#1D4749]`}>
                                    About {deity.title.length ? deity.title : <Skeleton variant="rounded" width={'200px'} />}
                                </h2>
                                <p className={`${noto_sans.className} font-[500] text-[16px] sm:text-[18px] text-[#5C5A58] leading-[1.6] mt-[0.75rem]`}>
                                    {deity.description.length ? deity.description : <Skeleton variant="text" width={'100%'} animation="wave" />}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Pray to section */}
                    <div className="w-full rounded-[24px] bg-white shadow-[0_10px_30px_rgba(0,0,0,0.08)] border border-[#F1D7B8]">
                        <div className="rounded-t-[24px] px-[1.25rem] py-[1rem]">
                            <div className={`${noto_sans.className} font-[700] text-[20px] sm:text-[22px] text-[#1D4749]`}>Pray to {deity.title || 'Deity'}</div>
                        </div>
                        <div className="px-[1rem] sm:px-[1.25rem] pb-[1.25rem] pt-[0.75rem]">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-[14px] sm:gap-[18px]">
                                {deity.prarthanas.length ? (
                                    Array.from(deity.prarthanas)
                                        .splice((paginationNumber - 1) * 4, 4)
                                        .map((prarthana: Prarthana, index: number) => {
                                            const trackNumber = index + 1 + 4 * (paginationNumber - 1);
                                            const title = `${trackNumber}. ${prarthana.title}`;
                                            return (
                                                <div
                                                    key={prarthana.id}
                                                    className="flex flex-col gap-1.5 bg-[#FFFFFF] transition-colors rounded-[18px] p-[0.50rem]"
                                                >
                                                    <AudioPlayer
                                                        src={prarthana.audio_url}
                                                        updateCurrentPrarthana={updateCurrentPrarthana}
                                                        prarthanaDetails={prarthana}
                                                        type="prarthana"
                                                        title={title}
                                                        maxWidth='600px'
                                                        maxHeight='100px'
                                                    />
                                                </div>
                                            );
                                        })
                                ) : (
                                    [1, 2, 3, 4].map((n) => {
                                        return (
                                            <div key={`deity-prarthana-skeleton-${n}`} className="bg-[#FFF9EB] border border-[#F6C756] rounded-[18px] p-[0.85rem] shadow-sm">
                                                <div className="grid gap-[14px]">
                                                    <div className={`${noto_sans.className} font-[600] text-[14px] text-[#1D4749]`}>
                                                        <Skeleton variant="text" width={'200px'} animation="wave" />
                                                    </div>
                                                    <Skeleton variant="rounded" width={'100%'} height={'80px'} animation="wave" />
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                            {deity.prarthanas.length > 4 && (
                                <div className="mt-[1.25rem] flex justify-center">
                                    <Pagination
                                        count={deity.prarthanas.length < 4 ? 1 : Math.ceil(deity.prarthanas.length / 4)}
                                        shape="rounded"
                                        page={paginationNumber}
                                        onChange={(event: React.ChangeEvent<unknown>, value: number) => {
                                            handlePageChange(value);
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default DeityView